<template>
  <div class="space-y-6">
    <!-- Uren Omzet Table -->
    <div
      class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden"
    >
      <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-2">
          <UsersIcon class="h-5 w-5 text-purple-500" />
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">
            Uren & Omzet per Afdeling
          </h3>
        </div>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Billable omzet per maand per medewerker, gegroepeerd per team
        </p>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-700/50 z-10"
              >
                Naam
              </th>
              <th
                v-for="month in data.months"
                :key="month"
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
              >
                {{ formatMonthHeader(month) }}
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Tot. uren
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Bill. uren
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Bill. omzet
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <template v-for="team in data.teams" :key="team.team_id">
              <!-- Team header row -->
              <tr
                class="bg-gray-100 dark:bg-gray-700 cursor-pointer hover:bg-gray-150 dark:hover:bg-gray-650"
                @click="toggleTeam(team.team_id)"
              >
                <td
                  class="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white sticky left-0 bg-gray-100 dark:bg-gray-700 z-10"
                >
                  <div class="flex items-center gap-2">
                    <ChevronRightIcon
                      class="h-4 w-4 transition-transform"
                      :class="{ 'rotate-90': expandedTeams.has(team.team_id) }"
                    />
                    {{ team.team_name }}
                    <span class="text-xs font-normal text-gray-500 dark:text-gray-400">
                      ({{ team.employees.length }})
                    </span>
                  </div>
                </td>
                <td
                  v-for="month in data.months"
                  :key="month"
                  class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white whitespace-nowrap"
                >
                  {{ formatCurrency(team.subtotals.months[month]?.billable_revenue) }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white"
                >
                  {{ formatHours(team.subtotals.total_hours) }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-right font-semibold text-gray-900 dark:text-white"
                >
                  {{ formatHours(team.subtotals.billable_hours) }}
                </td>
                <td
                  class="px-4 py-3 text-sm text-right font-semibold text-green-600 dark:text-green-400"
                >
                  {{ formatCurrency(team.subtotals.billable_revenue) }}
                </td>
              </tr>

              <!-- Employee rows -->
              <template v-if="expandedTeams.has(team.team_id)">
                <tr
                  v-for="emp in team.employees"
                  :key="emp.employee_id"
                  class="hover:bg-gray-50 dark:hover:bg-gray-700/30"
                >
                  <td
                    class="px-4 py-2.5 text-sm text-gray-900 dark:text-white pl-10 sticky left-0 bg-white dark:bg-gray-800 z-10"
                  >
                    <div>
                      {{ emp.employee_name }}
                      <span
                        v-if="emp.employee_function"
                        class="text-xs text-gray-500 dark:text-gray-400 ml-1"
                      >
                        {{ emp.employee_function }}
                      </span>
                    </div>
                  </td>
                  <td
                    v-for="month in data.months"
                    :key="month"
                    class="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"
                  >
                    {{ formatCurrency(emp.months[month]?.billable_revenue) }}
                  </td>
                  <td class="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">
                    {{ formatHours(emp.total_hours) }}
                  </td>
                  <td class="px-4 py-2.5 text-sm text-right text-gray-700 dark:text-gray-300">
                    {{ formatHours(emp.billable_hours) }}
                  </td>
                  <td
                    class="px-4 py-2.5 text-sm text-right font-medium text-green-600 dark:text-green-400"
                  >
                    {{ formatCurrency(emp.billable_revenue) }}
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
          <tfoot class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <td
                class="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white sticky left-0 bg-gray-50 dark:bg-gray-700/50 z-10"
              >
                Subtotaal uren
              </td>
              <td
                v-for="month in data.months"
                :key="month"
                class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white whitespace-nowrap"
              >
                {{ formatCurrency(data.totals.months[month]?.billable_revenue) }}
              </td>
              <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
                {{ formatHours(data.totals.total_hours) }}
              </td>
              <td class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white">
                {{ formatHours(data.totals.billable_hours) }}
              </td>
              <td class="px-4 py-3 text-sm text-right font-bold text-green-600 dark:text-green-400">
                {{ formatCurrency(data.totals.billable_revenue) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Derden Omzet Table -->
    <div
      v-if="derdenOmzetData?.revenue_groups?.length"
      class="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg overflow-hidden"
    >
      <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-2">
          <CurrencyEuroIcon class="h-5 w-5 text-violet-500" />
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">Derden Omzet</h3>
        </div>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Vaste-prijs diensten: totaal - gefactureerd - gecrediteerd, per omzetgroep
        </p>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-700/50 z-10"
              >
                Omzetgroep
              </th>
              <th
                v-for="month in derdenMonths"
                :key="month"
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap"
              >
                {{ formatMonthHeader(month) }}
              </th>
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                Totaal
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr
              v-for="group in derdenOmzetData.revenue_groups"
              :key="group.group_name"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/30"
            >
              <td
                class="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800 z-10"
              >
                {{ group.group_name }}
              </td>
              <td
                v-for="month in derdenMonths"
                :key="month"
                class="px-4 py-3 text-sm text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"
              >
                {{ formatCurrency(group.months[month]?.derden_omzet) }}
              </td>
              <td
                class="px-4 py-3 text-sm text-right font-semibold text-violet-600 dark:text-violet-400"
              >
                {{ formatCurrency(group.total_derden_omzet) }}
              </td>
            </tr>
            <tr
              v-if="derdenOmzetData.uitloopApplicable"
              class="border-t-2 border-violet-200 dark:border-violet-800 bg-violet-50/40 dark:bg-violet-900/10"
              :title="'Prognose voor de eerstvolgende open periode: vasteprijsdiensten met een Te factureren-datum in een al afgesloten maand'"
            >
              <td
                class="px-4 py-3 text-sm font-medium italic text-violet-700 dark:text-violet-300 sticky left-0 bg-violet-50 dark:bg-violet-900/30 z-10"
              >
                Derden Prognose NTF
              </td>
              <td
                v-for="month in derdenMonths"
                :key="month"
                class="px-4 py-3 text-sm text-right italic text-violet-700 dark:text-violet-300 whitespace-nowrap"
              >
                {{ formatCurrency(derdenOmzetData.totals?.uitloop?.[month]) }}
              </td>
              <td
                class="px-4 py-3 text-sm text-right font-semibold italic text-violet-700 dark:text-violet-300"
              >
                {{ formatCurrency(uitloopTotal) }}
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <td
                class="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white sticky left-0 bg-gray-50 dark:bg-gray-700/50 z-10"
              >
                Subtotaal derden
              </td>
              <td
                v-for="month in derdenMonths"
                :key="month"
                class="px-4 py-3 text-sm text-right font-bold text-gray-900 dark:text-white whitespace-nowrap"
              >
                {{ formatCurrency(derdenOmzetData.totals?.months[month]?.derden_omzet) }}
              </td>
              <td
                class="px-4 py-3 text-sm text-right font-bold text-violet-600 dark:text-violet-400"
              >
                {{ formatCurrency(derdenOmzetData.totals?.derden_omzet) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { UsersIcon, ChevronRightIcon, CurrencyEuroIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  data: { type: Object, required: true },
  derdenOmzetData: { type: Object, default: null }
})

const expandedTeams = ref(new Set())

const derdenMonths = computed(() => {
  return props.derdenOmzetData?.months || []
})

const uitloopTotal = computed(() => {
  const uitloop = props.derdenOmzetData?.totals?.uitloop || {}
  return Object.values(uitloop).reduce((sum, v) => sum + (Number(v) || 0), 0)
})

function toggleTeam(teamId) {
  if (expandedTeams.value.has(teamId)) {
    expandedTeams.value.delete(teamId)
  } else {
    expandedTeams.value.add(teamId)
  }
}

function formatCurrency(amount) {
  if (!amount) return '\u20AC 0'
  return `\u20AC ${new Intl.NumberFormat('nl-NL').format(Math.round(amount))}`
}

function formatHours(hours) {
  if (!hours) return '0'
  return new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(hours)
}

function formatMonthHeader(monthKey) {
  const [year, month] = monthKey.split('-')
  const monthNames = [
    'jan',
    'feb',
    'mrt',
    'apr',
    'mei',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec'
  ]
  return `${monthNames[parseInt(month) - 1]} '${year.slice(2)}`
}
</script>
